/**
 * EF-CLIENT-ONBOARDING-FRONTEND-V1: Client Onboarding Page
 * 
 * Route: /client/onboarding?token=...&user_id=...
 * Features:
 * - Validate token on load
 * - Show error if invalid/expired
 * - Password + confirm password form
 * - Submit -> POST /api/client/onboarding/complete
 * - Redirect to portal on success
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { 
  Loader2, Lock, AlertCircle, Building2, Eye, EyeOff, 
  CheckCircle2, ShieldCheck, XCircle 
} from "lucide-react";
import { toast } from "sonner";
import { clientOnboardingComplete } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";

// Token storage key for client portal
const CLIENT_TOKEN_KEY = "eventsflo_client_token";

// Password validation rules
const PASSWORD_MIN_LENGTH = 8;

const validatePassword = (password) => {
  const checks = {
    length: password.length >= PASSWORD_MIN_LENGTH,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
  const isValid = Object.values(checks).every(Boolean);
  return { checks, isValid };
};

export default function ClientOnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  
  // Get token and user_id from URL
  const token = searchParams.get("token");
  const userId = searchParams.get("user_id");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [validationState, setValidationState] = useState({ checks: {}, isValid: false });
  
  // Check for required parameters on mount
  useEffect(() => {
    if (!token || !userId) {
      setError("Invalid onboarding link. Please use the link from your invitation email.");
      console.log("[CLIENT ONBOARDING] Missing token or user_id in URL");
    }
  }, [token, userId]);
  
  // Update password validation on change
  useEffect(() => {
    setValidationState(validatePassword(password));
  }, [password]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate passwords
    if (!validationState.isValid) {
      setError("Password does not meet requirements");
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (!token || !userId) {
      setError("Invalid onboarding link");
      return;
    }
    
    setLoading(true);
    console.log("[CLIENT ONBOARDING] Completing onboarding for user:", userId);
    
    try {
      const response = await clientOnboardingComplete(token, userId, password);
      const data = response.data || response;
      
      if (data.success && data.access_token) {
        // Store token for auto-login
        localStorage.setItem(CLIENT_TOKEN_KEY, data.access_token);
        
        console.log("[CLIENT ONBOARDING] Account activated successfully");
        toast.success(t("clientPortal:onboarding.accountActivated"), {
          description: "Your password has been set. Redirecting to portal...",
        });
        
        // Redirect to client portal
        setTimeout(() => {
          navigate("/client", { replace: true });
        }, 1500);
      } else {
        setError(data.message || "Failed to complete account setup");
        console.log("[CLIENT ONBOARDING] Failed:", data.message);
      }
    } catch (err) {
      console.error("[CLIENT ONBOARDING] Error:", err);
      const errorMsg = err.response?.data?.detail || err.message || "Failed to complete account setup";
      setError(errorMsg);
      toast.error(t("clientPortal:onboarding.setupFailed"), { description: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // Show error state if no valid parameters
  if (!token || !userId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Card className="w-full max-w-md relative bg-card/80 backdrop-blur-sm border-white/10" data-testid="onboarding-error-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 rounded-full bg-red-500/10 border border-red-500/20">
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
            </div>
            <CardTitle className="text-xl text-red-400">Invalid Link</CardTitle>
            <CardDescription className="text-muted-foreground">
              This onboarding link is invalid or has expired. Please contact your administrator for a new invitation.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Button
              variant="outline"
              onClick={() => navigate("/client/login")}
              data-testid="go-to-login"
            >
              Go to Login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-sky-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-emerald-500/3 rounded-full blur-3xl" />
      </div>
      
      <Card className="w-full max-w-md relative bg-card/80 backdrop-blur-sm border-white/10" data-testid="onboarding-card">
        <CardHeader className="text-center pb-2">
          {/* Logo / Brand */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <ShieldCheck className="h-8 w-8 text-emerald-400" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">{t("clientPortal:onboarding.title")}</CardTitle>
          <CardDescription>
            Create a password to activate your Client Portal account
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive" className="border-red-500/50 bg-red-500/10" data-testid="onboarding-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm text-muted-foreground">
                Create Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-background/50"
                  disabled={loading}
                  data-testid="onboarding-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Password Requirements */}
            {password.length > 0 && (
              <div className="space-y-1 p-3 rounded-lg bg-background/30 border border-white/5" data-testid="password-requirements">
                <p className="text-xs text-muted-foreground mb-2">{t("clientPortal:onboarding.passwordRequirements")}</p>
                <PasswordCheck 
                  label={`At least ${PASSWORD_MIN_LENGTH} characters`} 
                  passed={validationState.checks.length} 
                />
                <PasswordCheck 
                  label="One uppercase letter" 
                  passed={validationState.checks.uppercase} 
                />
                <PasswordCheck 
                  label="One lowercase letter" 
                  passed={validationState.checks.lowercase} 
                />
                <PasswordCheck 
                  label="One number" 
                  passed={validationState.checks.number} 
                />
              </div>
            )}
            
            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm text-muted-foreground">
                Confirm Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 pr-10 bg-background/50"
                  disabled={loading}
                  data-testid="onboarding-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {/* Match indicator */}
              {confirmPassword.length > 0 && (
                <div className="flex items-center gap-1.5 text-xs mt-1">
                  {password === confirmPassword ? (
                    <>
                      <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                      <span className="text-emerald-400">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-3 w-3 text-red-400" />
                      <span className="text-red-400">Passwords do not match</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-4">
            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-500"
              disabled={loading || !validationState.isValid || password !== confirmPassword}
              data-testid="onboarding-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Activating account...
                </>
              ) : (
                <>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Activate Account
                </>
              )}
            </Button>
            
            <p className="text-xs text-center text-muted-foreground">
              Already have an account?{" "}
              <a href="/client/login" className="text-sky-400 hover:underline">
                Sign in
              </a>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

// Password check item component
function PasswordCheck({ label, passed }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {passed ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-400" />
      ) : (
        <XCircle className="h-3 w-3 text-muted-foreground" />
      )}
      <span className={passed ? "text-emerald-400" : "text-muted-foreground"}>
        {label}
      </span>
    </div>
  );
}
